import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Loader2,
  Download,
  Plus,
  Trash2,
  FileText,
  FileSpreadsheet,
  ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { trpc } from '@/lib/trpc';
import { usePermission } from '@/components/guards/permission-guard';
import { dateStamp } from '@/lib/download-xlsx';
import { downloadMovementsPdf, downloadMovementsXlsx } from '@/lib/cari-movements-export';
import { TRAVEL_COMPANY } from '@/lib/report-company';
import type { MovementDoc } from '@/lib/cari-movements-doc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { useSessionState } from '@/hooks/use-session-state';
import { TravelAddMovementDialog } from '@/components/travel/cari/add-movement-dialog';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { accountErrorKey } from '@/components/travel/cari/account-form-model';
import {
  selectClass,
  CURRENCIES,
  CURRENCY_NAMES,
  fmtMoney,
  useCariTypeLabel,
  useFiscalYear,
} from '../shared';

// Per-cari ledger (ekstre) with running balance per currency, within one
// fiscal year. Imported and GL-mirrored rows retain their audit trail.
export function TravelCariLedgerPage({
  account,
}: {
  account?: {
    source: 'customer' | 'partner';
    id: string;
    name: string;
    legacyCariCode: string | null;
    canExport: boolean;
  };
}) {
  const { t, i18n } = useTranslation('accounting');
  const { t: cariText } = useTranslation('travelCari');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const navigate = useNavigate();
  const { cariCode: routeCode } = useParams<{ cariCode: string }>();
  const cariCode = account ? account.legacyCariCode : routeCode;
  const [searchParams, setSearchParams] = useSearchParams();
  const typeLabel = useCariTypeLabel();
  const { years } = useFiscalYear();
  const canCreate = usePermission('ACCOUNTING', 'CREATE', 'TRAVEL');
  const canDelete = usePermission('ACCOUNTING', 'DELETE', 'TRAVEL');
  const accountingExport = usePermission('ACCOUNTING', 'EXPORT', 'TRAVEL');
  const canExport = account ? account.canExport : accountingExport;
  const [addOpen, setAddOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const yearParam = Number(searchParams.get('year'));
  const [fiscalYear, setFiscalYear] = useState<number>(
    Number.isInteger(yearParam) && yearParam > 2000 ? yearParam : new Date().getFullYear()
  );
  const filterKey = account ? `${account.source}:${account.id}` : (cariCode ?? '');
  const [dateFrom, setDateFrom] = useSessionState(`travel:cari:${filterKey}:dateFrom`, '');
  const [dateTo, setDateTo] = useSessionState(`travel:cari:${filterKey}:dateTo`, '');
  const [currency, setCurrency] = useSessionState(`travel:cari:${filterKey}:currency`, '');
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const invalidDateRange = !!dateFrom && !!dateTo && dateFrom > dateTo;
  useEffect(() => {
    setPage(1);
  }, [dateFrom, dateTo, currency, fiscalYear]);
  useEffect(() => {
    const year = Number(searchParams.get('year'));
    if (Number.isInteger(year) && year >= 2000 && year <= 2100) setFiscalYear(year);
    else if (years[0]) setFiscalYear(years[0]);
  }, [years[0], searchParams]);

  const utils = trpc.useUtils();

  const baseInput = {
    cariCode: cariCode ?? '',
    fiscalYear,
    currency: (currency || undefined) as (typeof CURRENCIES)[number] | undefined,
    dateFrom: dateFrom ? new Date(dateFrom) : undefined,
    dateTo: dateTo ? new Date(`${dateTo}T23:59:59.999Z`) : undefined,
  };

  const legacyLedger = trpc.travelCari.movements.useQuery(
    { ...baseInput, page },
    { enabled: !account && !!cariCode && !invalidDateRange }
  );
  const scopedInput = {
    source: account?.source ?? ('partner' as const),
    id: account?.id ?? '',
    fiscalYear,
    currency: baseInput.currency,
    dateFrom: baseInput.dateFrom,
    dateTo: baseInput.dateTo,
  };
  const accountLedger = trpc.travelCari.accounts.statement.useQuery(
    { ...scopedInput, page },
    { enabled: !!account && !invalidDateRange }
  );
  const { data, isLoading, error, refetch } = account ? accountLedger : legacyLedger;

  const buildDoc = (
    res: Awaited<ReturnType<typeof utils.travelCari.exportCariMovements.fetch>>
  ): MovementDoc => {
    const fmtDay = (val: string) => new Date(val).toLocaleDateString(dateLocale);
    const periodParts = [
      String(fiscalYear),
      dateFrom || dateTo
        ? `${dateFrom ? fmtDay(dateFrom) : '…'} – ${dateTo ? fmtDay(dateTo) : '…'}`
        : '',
    ].filter(Boolean);
    return {
      company: TRAVEL_COMPANY,
      meta: {
        mode: 'statement',
        title: t('cari.statementTitle', 'Account Statement').toLocaleUpperCase(
          i18n.language === 'tr' ? 'tr-TR' : 'en-US'
        ),
        periodLabel: periodParts.join('  ·  '),
        generatedLabel: t('cari.exportGenerated', 'Generated {{ts}}', {
          ts: new Date().toLocaleString(dateLocale),
        }),
        params: [
          { label: t('cari.fiscalYear', 'Fiscal Year'), value: String(fiscalYear) },
          { label: t('cari.currency', 'Currency'), value: currency || t('cari.all', 'All') },
        ],
        columns: {
          no: '#',
          date: t('cari.date', 'Date'),
          cari: t('cari.cari', 'Cari'),
          type: t('cari.type', 'Type'),
          code: t('cari.cariNo', 'Cari No'),
          voucher: t('cari.voucherNo', 'Voucher No'),
          description: t('cari.description', 'Description'),
          currency: t('cari.currency', 'Currency'),
          fxDebit: t('cari.fxDebit', 'FX Debit'),
          fxCredit: t('cari.fxCredit', 'FX Credit'),
          tryDebit: t('cari.tryDebit', 'TL Debit'),
          tryCredit: t('cari.tryCredit', 'TL Credit'),
          balance: t('cari.balance', 'Balance'),
        },
        totalLabel: t('cari.total', 'TOTAL'),
        balanceLabel: t('cari.balance', 'BALANCE'),
        debitSide: t('cari.debitSide', '(B)'),
        creditSide: t('cari.creditSide', '(A)'),
        manualTag: 'M',
        emptyLabel: t('cari.noMovements', 'No movements found'),
        pageLabel: (p, tt) =>
          t('cari.exportPage', 'Page {{page}} / {{total}}', { page: p, total: tt }),
        currencyNames: CURRENCY_NAMES,
        balanceNative: true, // travel ekstre shows native-currency running balance (matches the screen)
      },
      data: {
        rows: res.rows,
        totalsByCurrency: res.totalsByCurrency,
        cari: {
          code: res.cari.cariCode,
          name: res.cari.counterpartyName,
          type: `${res.cari.cariType} · ${typeLabel(res.cari.cariType)}`,
        },
      },
    };
  };

  const runExport = async (kind: 'pdf' | 'xlsx') => {
    if (!cariCode && !account) return;
    setExporting(true);
    try {
      const res = account
        ? await utils.travelCari.accounts.exportStatement.fetch(scopedInput)
        : await utils.travelCari.exportCariMovements.fetch(baseInput);
      const safe = (res.cari.counterpartyName || 'cari').replace(/[\\/?*[\]:]/g, ' ').slice(0, 24);
      const doc = buildDoc(res);
      const base = `travel-cari-ekstre-${safe}-${fiscalYear}-${dateStamp()}`;
      if (kind === 'pdf') await downloadMovementsPdf(doc, `${base}.pdf`);
      else await downloadMovementsXlsx(doc, `${base}.xlsx`);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const del = trpc.travelCari.deleteMovement.useMutation({
    onSuccess: () => {
      utils.travelCari.movements.invalidate();
      utils.travelCari.allMovements.invalidate();
      utils.travelCari.balances.invalidate();
      utils.travelCari.cariMizan.invalidate();
      utils.travelCari.accounts.statement.invalidate();
      setDeleteId(null);
      showSuccessToast(t('cari.movementDeleted', 'Movement deleted'));
    },
    onError: (e) =>
      showErrorToast(
        cariText(
          `account.errors.${e.message === 'POSTED_MOVEMENT_DELETE_FORBIDDEN' ? 'postedMovement' : accountErrorKey(e)}`
        )
      ),
  });

  const items = data?.items ?? [];
  const pag = data?.pagination;
  const colCount = canDelete ? 10 : 9;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {!account && (
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-bold">
              {account
                ? t('cari.ledgerTitle', 'Account Ledger')
                : (data?.cari.counterpartyName ?? t('cari.ledgerTitle', 'Account Ledger'))}
            </h1>
            <p className="text-muted-foreground">
              {t('cari.ledgerSubtitle', 'Current-account movements')}
              {data?.cari
                ? ` · ${data.cari.cariCode} · ${data.cari.cariType} ${typeLabel(data.cari.cariType)}`
                : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canExport && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  disabled={exporting || isLoading || !!error || !data || invalidDateRange}
                >
                  {exporting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="mr-2 h-4 w-4" />
                  )}
                  {t('cari.export', 'Export')}
                  <ChevronDown className="ml-2 h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => runExport('pdf')}>
                  <FileText className="h-4 w-4 text-red-600" />
                  {t('cari.exportPdf', 'PDF')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => runExport('xlsx')}>
                  <FileSpreadsheet className="h-4 w-4 text-green-600" />
                  {t('cari.exportExcel', 'Excel')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {canCreate && !error && !isLoading && (data?.cari || account) && (
            <Button onClick={() => setAddOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('cari.addMovement', 'Add Movement')}
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className="border-destructive/30 flex items-center gap-3 border p-4 text-sm"
        >
          <p>{t('cari.loadFailed', 'Could not load movements.')}</p>
          <Button
            variant="outline"
            onClick={() => {
              void refetch();
            }}
          >
            {t('cari.retry', 'Retry')}
          </Button>
        </div>
      )}
      {/* Per-currency summary */}
      {data && data.summaryByCurrency.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {data.summaryByCurrency.map((s) => {
            const net = s.currency === 'TRY' ? s.tryClosing : s.fxClosing;
            const debit = s.currency === 'TRY' ? s.tryDebit : s.fxDebit;
            const credit = s.currency === 'TRY' ? s.tryCredit : s.fxCredit;
            return (
              <div key={s.currency} className="bg-card rounded-lg border p-4">
                <p className="text-muted-foreground text-xs font-medium">
                  {s.currency} · {cariText('account.closingBalance')}
                </p>
                <p className="mt-1 text-xl font-bold tabular-nums">{fmtMoney(net)}</p>
                <p className="text-muted-foreground mt-1 text-xs">
                  {cariText('account.openingBalance')}:{' '}
                  {fmtMoney(s.currency === 'TRY' ? s.tryOpening : s.fxOpening)}
                </p>
                <p className="text-muted-foreground mt-1 text-xs">
                  {t('cari.debitShortLabel', 'Debit')}: {fmtMoney(debit)} ·{' '}
                  {t('cari.creditShortLabel', 'Credit')}: {fmtMoney(credit)}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Filters */}
      {canCreate && (
        <p className="text-muted-foreground text-xs">{cariText('account.correctionHint')}</p>
      )}
      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <Label className="text-xs">{t('cari.fiscalYear', 'Fiscal Year')}</Label>
          <select
            value={fiscalYear}
            onChange={(e) => {
              setFiscalYear(Number(e.target.value));
              setSearchParams((current) => {
                const next = new URLSearchParams(current);
                next.set('year', e.target.value);
                return next;
              });
              setPage(1);
            }}
            className={`${selectClass} block w-24`}
          >
            {[...new Set([fiscalYear, new Date().getFullYear(), ...years])]
              .sort((a, b) => b - a)
              .map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('cari.currency', 'Currency')}</Label>
          <select
            value={currency}
            onChange={(e) => {
              setCurrency(e.target.value);
              setPage(1);
            }}
            className={`${selectClass} block w-28`}
          >
            <option value="">{t('cari.all', 'All')}</option>
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('cari.dateFrom', 'From')}</Label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
            className="w-36"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('cari.dateTo', 'To')}</Label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
            className="w-36"
          />
        </div>
      </div>

      {invalidDateRange && (
        <p role="alert" className="text-destructive text-sm">
          {cariText('account.validation.dateRange')}
        </p>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 border-b">
              <th className="px-3 py-3 text-left font-medium">{t('cari.date', 'Date')}</th>
              <th className="px-3 py-3 text-left font-medium">
                {t('cari.voucherNo', 'Voucher No')}
              </th>
              <th className="px-3 py-3 text-left font-medium">
                {t('cari.description', 'Description')}
              </th>
              <th className="px-3 py-3 text-center font-medium">
                {t('cari.currency', 'Currency')}
              </th>
              <th className="px-3 py-3 text-right font-medium">{t('cari.fxDebit', 'FX Debit')}</th>
              <th className="px-3 py-3 text-right font-medium">
                {t('cari.fxCredit', 'FX Credit')}
              </th>
              <th className="px-3 py-3 text-right font-medium">{t('cari.tryDebit', 'TL Debit')}</th>
              <th className="px-3 py-3 text-right font-medium">
                {t('cari.tryCredit', 'TL Credit')}
              </th>
              <th className="px-3 py-3 text-right font-medium">{t('cari.balance', 'Balance')}</th>
              {canDelete && <th className="px-3 py-3" />}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={colCount} className="py-8 text-center">
                  <Loader2 className="mx-auto h-6 w-6 animate-spin" />
                </td>
              </tr>
            )}
            {!isLoading && !error && !invalidDateRange && items.length === 0 && (
              <tr>
                <td colSpan={colCount} className="text-muted-foreground py-8 text-center">
                  {t('cari.noMovements', 'No movements found')}
                </td>
              </tr>
            )}
            {items.map((m) => (
              <tr key={m.id} className="hover:bg-muted/30 border-b last:border-0">
                <td className="whitespace-nowrap px-3 py-2.5">
                  {new Date(m.date).toLocaleDateString(dateLocale)}
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs">
                  {m.voucherNo}
                  {m.isManual && (
                    <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-700">
                      M
                    </span>
                  )}
                  {m.isOpeningCarryForward && (
                    <span className="ml-1.5 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] text-blue-700">
                      {t('cari.openingShort', 'DEVIR')}
                    </span>
                  )}
                </td>
                <td className="max-w-[320px] px-3 py-2.5">
                  <span className="block truncate">{m.description}</span>
                </td>
                <td className="px-3 py-2.5 text-center">{m.currency}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {m.fxDebit > 0 ? fmtMoney(m.fxDebit) : ''}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {m.fxCredit > 0 ? fmtMoney(m.fxCredit) : ''}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {m.tryDebit > 0 ? fmtMoney(m.tryDebit) : ''}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {m.tryCredit > 0 ? fmtMoney(m.tryCredit) : ''}
                </td>
                <td className="px-3 py-2.5 text-right font-medium tabular-nums">
                  {fmtMoney(m.currency === 'TRY' ? m.tryBalance : m.fxBalance)}
                </td>
                {canDelete && (
                  <td className="px-3 py-2.5 text-right">
                    {m.canDelete && (
                      <button
                        onClick={() => setDeleteId(m.id)}
                        disabled={del.isPending}
                        aria-label={t('cari.delete', 'Delete')}
                        className="text-muted-foreground hover:text-red-600"
                        title={t('cari.delete', 'Delete')}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pag && pag.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-sm">
            {(page - 1) * pag.pageSize + 1}-{Math.min(page * pag.pageSize, pag.total)} / {pag.total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              {t('cari.previous', 'Previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= pag.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              {t('cari.next', 'Next')}
            </Button>
          </div>
        </div>
      )}

      {(data?.cari || account) && (
        <TravelAddMovementDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          fixedCari={
            account
              ? { source: account.source, id: account.id, counterpartyName: account.name }
              : { cariCode: data!.cari.cariCode, counterpartyName: data!.cari.counterpartyName }
          }
        />
      )}
      <ConfirmationDialog
        open={!!deleteId}
        onOpenChange={(open) => {
          if (!open) setDeleteId(null);
        }}
        title={t('cari.delete', 'Delete')}
        description={t('cari.confirmDelete', 'Delete this movement?')}
        confirmLabel={t('cari.delete', 'Delete')}
        cancelLabel={t('cari.cancel', 'Cancel')}
        variant="destructive"
        isLoading={del.isPending}
        onConfirm={async () => {
          if (deleteId) await del.mutateAsync({ id: deleteId });
        }}
      />
    </div>
  );
}
